import React, { FC, useState } from 'react';
import { Button, Drawer } from 'antd';
import copy from 'copy-to-clipboard';
import { Close, Copy, Download } from 'common/svg';
import { dateFormatter } from 'common/helpers/date.helper';
import { priceFormatter } from 'common/helpers/format.helper';
import { nameBuilder } from 'common/helpers/name.helper';
import { EPurchaseOrderStatus, IPurchaseOrderModel } from 'entities/PurchaseOrder/PurchaseOrder.models';
import { PurchaseOrderStatus } from 'entities/PurchaseOrder/components/PurchaseOrderStatus';
import { purchaseOrderNumberBuilder } from 'entities/PurchaseOrder/PurchaseOrder.helper';
import { exportPurchaseOrderToExcel } from 'entities/PurchaseOrder/PurchaseOrder.export';
import { IUserModel } from 'entities/User/User.models';

interface IComponentProps {
  open: boolean;
  order: IPurchaseOrderModel | null;
  franchisee: IUserModel | null;
  onCancel: () => void;
}

export const PurchaseOrderCard: FC<IComponentProps> = ({ open, order, franchisee, onCancel }) => {
  const [exporting, setExporting] = useState(false);
  const items = order?.items.map((item, index) => ({ ...item, key: index }));
  const franchiseeName = nameBuilder(franchisee?.firstName, franchisee?.lastName);
  // Bugs Track PO #7: delivered = DR view (SO/SI/Var), any other status = sales-order view.
  const isDelivered = order?.status === EPurchaseOrderStatus.Delivered;

  const copySonum = (e: Event) => {
    e.stopPropagation();

    if (order) {
      copy(order.sonum);
    }
  };

  const handleExport = async () => {
    if (!order) {
      return;
    }

    setExporting(true);

    try {
      await exportPurchaseOrderToExcel(order, franchiseeName);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Drawer
      rootClassName="purchase-order-card"
      open={open}
      onClose={onCancel}
      height="100vh"
      placement="bottom"
      title="Order Information"
      closeIcon={null}
      classNames={{ body: 'scrollbar-none' }}
      extra={<Button className="btn-gray h-44 square" icon={<Close className="icon-gray" />} onClick={onCancel} />}
      footer={
        <div className="flex flex-justify-between">
          <Button className="btn-secondary" icon={<Download className="icon-gray" />} loading={exporting} onClick={handleExport}>
            Export Excel
          </Button>
          <Button className="btn-primary" onClick={onCancel}>
            Back
          </Button>
        </div>
      }
    >
      <div className="flex purchase-order-card__container purchase-order-card__details">
        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Order Details</div>

          {order?.orderedAt && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Ordered</div>
              <span className="ff-regular fs-xxs">{dateFormatter(order.orderedAt, 'MM/DD/YYYY HH:mm')}</span>
            </div>
          )}

          {order?.deliveredAt && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Delivered</div>
              <span className="ff-regular fs-xxs">{dateFormatter(order.deliveredAt, 'MM/DD/YYYY HH:mm')}</span>
            </div>
          )}
        </div>

        <div className="flex__item_1">
          {order?.status && (
            <div className="mb-5">
              <PurchaseOrderStatus status={order.status} />
            </div>
          )}

          {order && (
            <div className="flex flex-align-end">
              <span className="ff-medium fs-xxs">{purchaseOrderNumberBuilder(order.sonum)}</span>
              <Copy className="icon-gray" onClick={copySonum} />
            </div>
          )}
        </div>
      </div>

      <div className="flex purchase-order-card__container purchase-order-card__details">
        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Franchisee Details</div>

          <div className="mb-4">
            <div className="mb-2 ff-regular fs-xxxs color-gray-1">Name</div>
            <span className="ff-regular fs-xxs">{franchiseeName}</span>
          </div>
        </div>

        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Payment Details</div>

          {items && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Items</div>
              <span className="ff-regular fs-xxs">{items.length}</span>
            </div>
          )}

          {order && (
            <div>
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Amount (Total)</div>
              <span className="ff-regular fs-xxs">{priceFormatter(order.amount)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="purchase-order-card__container">
        <div className="mb-5 ff-medium fs-xxs">{isDelivered ? 'Delivery Receipt Details' : 'Sales Order Details'}</div>

        {items?.map((item) => {
          return (
            <div key={item.key} className="mb-4 purchase-order-card__item">
              <div className="mb-4 p-2 flex flex-justify-between">
                <div>
                  <div className="mb-2 ff-regular fs-xxxs color-gray">Item</div>
                  <span className="ff-regular fs-xxs">{item.name}</span>
                </div>
                <div className="ml-3 t-align-e">
                  <div className="mb-2 ff-regular fs-xxxs color-gray">Unit Cost</div>
                  <span className="ff-regular fs-xxs">{priceFormatter(item.price)}</span>
                </div>
              </div>

              {isDelivered ? (
                <div className="purchase-order-card__item-table">
                  <div className="purchase-order-card__item-table-header-cell">SO Qty</div>
                  <div className="purchase-order-card__item-table-header-cell">SI Qty</div>
                  <div className="purchase-order-card__item-table-header-cell">Var</div>
                  <div className="purchase-order-card__item-table-header-cell t-align-e">Adjusted Total Cost</div>
                  {/* MBC-83: SI Qty / Var / Adjusted Total Cost from BE-computed siQty/variance/adjustedTotalCost. */}
                  <div className="purchase-order-card__item-table-body-cell">{item.orderCount}</div>
                  <div className="purchase-order-card__item-table-body-cell">{item.siQty}</div>
                  <div className="purchase-order-card__item-table-body-cell">{item.variance}</div>
                  <div className="purchase-order-card__item-table-body-cell t-align-e">
                    {priceFormatter(item.adjustedTotalCost)}
                  </div>
                </div>
              ) : (
                <div className="purchase-order-card__item-table purchase-order-card__item-table--so">
                  <div className="purchase-order-card__item-table-header-cell">Quantity</div>
                  <div className="purchase-order-card__item-table-header-cell t-align-e">Total Cost</div>
                  <div className="purchase-order-card__item-table-body-cell">{item.orderCount}</div>
                  <div className="purchase-order-card__item-table-body-cell t-align-e">
                    {priceFormatter(item.adjustedTotalCost)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};
